/**
 * Watchlist management bottom sheet presentation.
 *
 * Composed on the shared MobileSheetChrome primitive (SE-5547), which owns
 * the sheet surface, ✕ close chip, height cap, and the backdrop/✕/ESC/back
 * dismissal paths. Gesture dismissal is disabled (`disableSwipeDismiss`)
 * because the body's DraggableFlatList owns vertical pan for drag-to-reorder
 * — the former content-wide PanGestureHandler contended with it. The bespoke
 * desktop `w-1/2 self-center` constraint, isDark background forks, and the
 * `max-h-[40vh]` cap are gone: the chrome owns surface and height.
 * @module components/organisms/ManageActionSheet
 */

import { PlusIcon } from "lucide-react-native";
import React, { memo } from "react";
import { Platform } from "react-native";
import DraggableFlatList, {
  DragEndParams,
  RenderItem,
} from "react-native-draggable-flatlist";
import { GestureHandlerRootView } from "react-native-gesture-handler";

import CustomPromptContainer from "@/components/molecules/CustomPrompt";
import { CustomPromptRefProps } from "@/components/molecules/CustomPrompt/CustomPromptContainer";
import MobileSheetChrome from "@/components/molecules/MobileSheetChrome";
import { Button } from "@/components/atoms/Button";
import { Divider } from "@/components/atoms/Divider";
import { FlatList } from "@/components/atoms/FlatList";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

import type { ListRenderItem } from "@/components/atoms/FlashList";

import { WatchlistWatchlistMetaFragment } from "@/generated/graphql";
import defaultExtractKey from "@/utils/defaultExtractKey";

/**
 * Props for ManageActionSheetView
 */
interface Props {
  showActionsheet: boolean;
  onClose: () => void;
  watchlists: WatchlistWatchlistMetaFragment[];
  onAddWatchlistPress: () => void;
  loading: boolean;
  onDragEnd?: (params: DragEndParams<WatchlistWatchlistMetaFragment>) => void;
  positionsUpdating?: boolean;
  renderItem: RenderItem<WatchlistWatchlistMetaFragment>;
  promptRef?: React.RefObject<CustomPromptRefProps>;
  isSmallScreen: boolean;
  /**
   * Render adapter for the WEB FlatList, built by the Container.
   *
   * The web list has no drag API, so the adapter feeds `renderItem` a no-op
   * `drag`, `isActive: false` and a no-op `getIndex`. Its identity has to stay
   * stable across renders — FlatList re-renders every row when it changes —
   * which is why the View used to hold three `useCallback`s to build it
   * (SE-8019).
   */
  renderWebItem: ListRenderItem<WatchlistWatchlistMetaFragment>;
}

/**
 * Presentational component that renders the watchlist management sheet.
 * Displays a draggable list of watchlists with create, rename, and delete
 * capabilities on the shared MobileSheetChrome.
 * @param root0 - Component props
 * @param root0.showActionsheet - Whether the sheet is currently visible
 * @param root0.onClose - Callback invoked when the sheet should be closed
 * @param root0.watchlists - Array of user's watchlists to display
 * @param root0.onAddWatchlistPress - Callback invoked when "New List" button is pressed
 * @param root0.loading - Whether a watchlist creation operation is in progress
 * @param root0.onDragEnd - Callback invoked when watchlist drag-and-drop reordering completes (native only)
 * @param root0.positionsUpdating - Whether watchlist position updates are in progress
 * @param root0.renderItem - Function to render each watchlist item in the list
 * @param root0.promptRef - Ref to custom prompt component for watchlist rename/delete confirmations
 * @param root0.renderWebItem - Render adapter for the web FlatList
 * @param root0.isSmallScreen - Whether the screen size is small (affects control sizing)
 */
const ManageActionSheetView = ({
  showActionsheet,
  onClose,
  watchlists,
  onAddWatchlistPress,
  loading,
  onDragEnd,
  positionsUpdating,
  renderItem,
  promptRef,
  isSmallScreen,
  renderWebItem,
}: Props) => (
  <>
    <MobileSheetChrome
      isOpen={showActionsheet}
      onClose={onClose}
      title="Manage Watchlists"
      disableSwipeDismiss
      headerContent={
        /* Done — pinned with the title in the chrome's sticky header band */
        <Stack direction="horizontal" className="items-center justify-end">
          <Button
            label="Done"
            variant="link"
            labelTone="info"
            labelVariant="body"
            onPress={onClose}
            size="lg"
          />
        </Stack>
      }
      footer={
        <Button
          label={loading || positionsUpdating ? "Please Wait..." : "New List"}
          icon={PlusIcon}
          labelTone="info"
          iconTone="info"
          labelVariant="body"
          iconSize={!isSmallScreen ? "md" : "lg"}
          isDisabled={loading || positionsUpdating}
          variant="solid"
          onPress={onAddWatchlistPress}
          className="gap-3 bg-surface-action"
          size={!isSmallScreen ? "md" : "lg"}
        />
      }
    >
      <Stack className="w-full gap-4">
        <Text
          variant="title-sm"
          className="pb-4 text-center text-content-secondary"
        >
          {Platform.OS === "web"
            ? "Create, rename, and delete watchlists."
            : "Create, rename, reorder, and delete watchlists."}
        </Text>

        {Platform.OS === "web" ? (
          <FlatList
            contentContainerClassName="flex-grow rounded-lg bg-surface-subtle"
            data={watchlists}
            keyExtractor={defaultExtractKey}
            renderItem={renderWebItem}
            ItemSeparatorComponent={Divider}
            scrollEventThrottle={16}
          />
        ) : (
          /* GestureHandlerRootView keeps drag-to-reorder alive inside the
           * chrome's RN Modal hierarchy (RNGH gestures need a root view
           * within each native modal on Android). */
          <GestureHandlerRootView>
            <DraggableFlatList
              contentContainerClassName="flex-grow rounded-lg bg-surface-subtle"
              data={watchlists}
              keyExtractor={defaultExtractKey}
              renderItem={renderItem}
              onDragEnd={onDragEnd}
              automaticallyAdjustContentInsets
              ItemSeparatorComponent={Divider}
              scrollEventThrottle={16}
              decelerationRate="normal"
            />
          </GestureHandlerRootView>
        )}
      </Stack>
    </MobileSheetChrome>
    {/* CustomPrompt is its own overlay (Actionsheet with useRNModal on
     * native / OverlayProvider portal on web), so it presents above the
     * chrome regardless of tree position. */}
    <CustomPromptContainer ref={promptRef} />
  </>
);

ManageActionSheetView.displayName = "ManageActionSheetView";

export default memo(ManageActionSheetView);
